// @ts-nocheck
import _ from 'lodash';
import { isWithdrawn } from '@staty/shared/utils/registrationTeamStatus';
import helpers from './helpers';

/**
 * The counts on `/admin/registration/:id`.
 *
 * Two different questions share the word "count", and Withdrawn answers them
 * differently. Keeping both rules in one file is the point — they were previously
 * two inline filters ~360 lines apart, and the first one got read as the general
 * rule.
 *
 *  1. **"How many registrations did we receive?"** — the Registrations and Players
 *     cards. A withdrawn team **counts**: it really did register, and its money is
 *     already in Total Payments right beside it. Excluding it produced the report
 *     that prompted this — `Registrations 0` sitting next to `Total Payments
 *     $280.00`, with both paid teams listed underneath.
 *
 *  2. **"How full is this division?"** — the per-division chips. A withdrawn team
 *     does **not** count: it holds no seat. The capacity bar drawn directly under
 *     each chip comes from `deriveCapacityStates`, which drops withdrawn teams
 *     entirely, so a chip that counted them would contradict its own bar.
 *
 * Neither of these touches capacity itself. Seats live in
 * `packages/shared/utils/waitlist.ts` and are unaffected by anything here.
 * See ADR 0031 (and ADR 0027 for the status model).
 */

/**
 * Headline card numbers. Both are a record of registrations RECEIVED, so this
 * deliberately applies **no status filter at all** — withdrawn teams are counted.
 * Don't add one; that's the bug this file exists to prevent.
 *
 * @param teams   registration teams already narrowed by the page's filters
 *                (format, payment status, acceptance, division, search)
 * @param players players for the registration, already narrowed to real entries
 */
export function deriveRegistrationCounts({ teams = [], players = [] } = {}) {
  const teamIds = new Set(_.map(teams, (t) => String(_.get(t, '_id'))));

  // Players follow their team: a player counts iff their team is in `teams`.
  // This intersection is also what keeps Players consistent with Registrations
  // under every filter, rather than needing its own parallel filter chain.
  const matchedPlayers = _.filter(players, (player) =>
    teamIds.has(String(helpers.getObjId(_.get(player, 'registrationTeam')))));

  return {
    registrations: teams.length,
    players: matchedPlayers.length,
    matchedPlayers,
  };
}

/**
 * Teams occupying a division, for the per-division chip headcount. Withdrawn
 * teams ARE excluded here — see (2) above. Matches on the assigned division,
 * falling back to the requested one for teams not yet placed.
 */
export function teamsOccupyingDivision(teams = [], divisionId) {
  return _.filter(teams, (rt) => {
    if (isWithdrawn(rt)) return false;
    const id = _.get(rt, 'division._id') || _.get(rt, 'requestedDivision._id');
    return id === divisionId;
  });
}
